import { Typography, toast } from '@neo4j-ndl/react';
import { useLogger } from '@nx/logger/react';
import type { DbmsInfo } from '@nx/state/desktop';
import {
  DBMS_STATUS,
  getRelateErrors,
  useDbmsList,
  useStartDbmsMutation,
  useStopDbms,
  useStopDbmsMutation,
} from '@nx/state/desktop';
import { OpenPathButton } from '@nx/ui';
import { useEffect, useState } from 'react';

import { useDbmsConnection } from './use-dbms-connection';

export const useToggleInstanceRunStatus = (instance: DbmsInfo) => {
  const logger = useLogger();
  const { dbmss } = useDbmsList();
  const [isStarting, setIsStarting] = useState(false);

  // `fixedCacheKey` is used to subscribe to the mutations' cache. This is so we
  // can detect mutations that have been triggered elsewhere that start/stop
  // this instance.
  const [startDbms, startResult] = useStartDbmsMutation({ fixedCacheKey: `dbms-start-${instance.id}` });
  // We don't use the mutation trigger for this mutation, because we want a
  // trigger that can stop multiple instances and always set the correct
  // `fixedCacheKey` for the instance that it's stopping. The trigger from
  // `useStopDbms` does exactly that.
  const [, stopResult] = useStopDbmsMutation({ fixedCacheKey: `dbms-stop-${instance.id}` });
  const stopDbms = useStopDbms();

  const { disconnect } = useDbmsConnection({
    id: instance.id,
    name: instance.name,
    connectionUri: instance.connectionUri,
    dbmsIsRunning: instance.status === DBMS_STATUS.STARTED,
    credentialsType: 'dbms',
  });

  useEffect(() => {
    const result = startResult.isError ? startResult : stopResult;
    if (result.isError) {
      const errors = getRelateErrors(result.error);
      const toastMessage = (
        <span>
          <Typography variant="subheading-medium">{`Something went wrong while ${startResult.isError ? 'starting' : 'stopping'} instance "${instance.name}"`}</Typography>
          <br />
          {errors}
          <span className="mt-2 flex justify-end">
            <OpenPathButton path={[instance.rootPath ?? '', 'logs', 'neo4j.log']}>Locate log file</OpenPathButton>
          </span>
        </span>
      );
      toast.danger(toastMessage, { isCloseable: true });
      result.reset();
    }
  }, [startResult, stopResult, instance]);

  const toggleRunStatus =
    instance.status === DBMS_STATUS.STOPPED
      ? async () => {
          // Disconnect if we're currently connected, stop running DBMSs if
          // there are any, and then start the instance. This is to have the
          // same behavior as Desktop 1.x.
          setIsStarting(true);
          disconnect();
          const runningDbmss = dbmss.filter((dbms) => dbms.status === DBMS_STATUS.STARTED);
          const promises = runningDbmss.map(async (dbms) => {
            logger.info(`DBMS ${dbms.id} is running, attempting to stop it`);
            await stopDbms(dbms.id);
          });
          await Promise.allSettled(promises);

          await startDbms(instance.id);
          setIsStarting(false);
        }
      : async () => {
          disconnect();
          await stopDbms(instance.id);
        };

  return {
    toggleRunStatus,
    isStopped: instance.status === DBMS_STATUS.STOPPED,
    isStarted: instance.status === DBMS_STATUS.STARTED,
    isStarting: isStarting || startResult.isLoading,
    isStopping: stopResult.isLoading,
  };
};
